import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { ClientSplitAssetLineRequest, ClientSplitAssetLineResponse } from '@siliconx/shared/dto/client-asset';
import { Button } from '@siliconx/shared/ui/Button';
import { authFetch, API_BASE } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { useModalTrap } from '../../routes/assets/useModalTrap';

/**
 * SIL-1648: give serials to some units of a stock line (quantity > 1).
 * POST /api/client/assets/:id/split splits `units` serialized assets off the
 * line. Admin-only on the server (403 admin_only); the caller also hides the
 * action for non-admins.
 */

/** Human text per server error code. Unknown codes fall back to a generic line. */
export const SPLIT_ERROR_MESSAGES = {
  serials_required: 'Enter at least one serial number.',
  invalid_units: 'Enter a whole number of units, at least 1 and less than the line quantity.',
  serial_count_mismatch: 'The number of serials must match the number of units.',
  blank_serial: 'A serial number cannot be blank.',
  admin_only: 'Only an organization admin can add serials.',
  asset_not_found: 'This asset could not be found.',
  open_listing: 'This line has an open listing. Close the listing first.',
  asset_sold: 'This line has been sold.',
  not_a_line: 'This asset is a single unit, not a stock line.',
  not_splittable: 'This line cannot be split.',
  duplicate_serial: 'A serial number appears more than once in your list.',
  serial_in_use: 'One of these serial numbers is already used by another asset.',
  unit_serials_exceed_remaining:
    'The remaining units would be fewer than the serials already entered on this line. Enter or remove serials first.',
  unit_serial_not_in_split: 'Every serial already entered on this line must be included when splitting the whole line.',
} as const;

const GENERIC_ERROR = 'Could not add serials. Try again.';

export class SplitError extends Error {
  constructor(public readonly code: string | null) {
    super(code ?? 'split_failed');
  }
}

/** Serials typed one per line. Blank lines at the ends are ignored; inner blanks are kept so they surface as blank_serial. */
export function parseSerialLines(text: string): string[] {
  const lines = text.split(/\r?\n/).map((l) => l.trim());
  while (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();
  while (lines.length > 0 && lines[0] === '') lines.shift();
  return lines;
}

async function postSplit(assetId: string, body: ClientSplitAssetLineRequest): Promise<ClientSplitAssetLineResponse> {
  const res = await authFetch(`${API_BASE}/api/client/assets/${encodeURIComponent(assetId)}/split`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as { error?: { code?: string } | string };
    const code = typeof json.error === 'string' ? json.error : (json.error?.code ?? null);
    throw new SplitError(code);
  }
  return (await res.json()) as ClientSplitAssetLineResponse;
}

export function AddSerialsDialog({
  assetId,
  lineQuantity,
  onClose,
}: {
  assetId: string;
  lineQuantity: number;
  onClose: () => void;
}) {
  const panelRef = useModalTrap(true);
  const queryClient = useQueryClient();
  const [unitsText, setUnitsText] = useState('');
  const [serialsText, setSerialsText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: ClientSplitAssetLineRequest) => postSplit(assetId, body),
    onSuccess: async () => {
      toast.success('Serials added.');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.assets.detail(assetId) }),
        queryClient.invalidateQueries({ queryKey: qk.assets.listAll() }),
        queryClient.invalidateQueries({ queryKey: qk.dashboard.summary() }),
      ]);
      onClose();
    },
    onError: (err: Error) => {
      const code = err instanceof SplitError ? err.code : null;
      setError((code && (SPLIT_ERROR_MESSAGES as Record<string, string>)[code]) || GENERIC_ERROR);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const units = Number(unitsText);
    if (!Number.isInteger(units) || units < 1 || units >= lineQuantity) {
      setError(SPLIT_ERROR_MESSAGES.invalid_units);
      return;
    }
    const serials = parseSerialLines(serialsText);
    if (serials.length === 0) {
      setError(SPLIT_ERROR_MESSAGES.serials_required);
      return;
    }
    if (serials.length !== units) {
      setError(`You entered ${serials.length} ${serials.length === 1 ? 'serial' : 'serials'} for ${units} ${units === 1 ? 'unit' : 'units'}. The counts must match.`);
      return;
    }
    if (serials.some((s) => s === '')) {
      setError(SPLIT_ERROR_MESSAGES.blank_serial);
      return;
    }
    if (new Set(serials).size !== serials.length) {
      setError(SPLIT_ERROR_MESSAGES.duplicate_serial);
      return;
    }
    mutation.mutate({ units, serials });
  }

  return createPortal(
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !mutation.isPending) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-serials-title"
        className="w-full max-w-md rounded-xl bg-card p-6 shadow-xl"
      >
      <form onSubmit={handleSubmit}>
        <h2 id="add-serials-title" className="mb-2 text-base font-semibold text-foreground">
          Add serials
        </h2>
        <p className="mb-3 text-sm text-muted-foreground">
          This line holds {lineQuantity} units. Pick how many to give serial numbers. Each becomes its own asset;
          the rest stay in the line.
        </p>
        <label htmlFor="add-serials-units" className="mb-1 block text-xs font-medium text-foreground">
          Units
        </label>
        <input
          id="add-serials-units"
          type="number"
          min={1}
          max={lineQuantity - 1}
          step={1}
          value={unitsText}
          onChange={(e) => setUnitsText(e.target.value)}
          className="mb-3 w-32 rounded-md border border-border bg-background px-2 py-1 text-sm"
        />
        <label htmlFor="add-serials-serials" className="mb-1 block text-xs font-medium text-foreground">
          Serial numbers (one per line)
        </label>
        <textarea
          id="add-serials-serials"
          value={serialsText}
          onChange={(e) => setSerialsText(e.target.value)}
          rows={6}
          spellCheck={false}
          autoComplete="off"
          className="mb-3 w-full rounded-md border border-border bg-background px-2 py-1 font-mono text-sm"
        />
        {error && (
          <p role="alert" className="mb-3 text-xs text-red-600">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving...' : 'Add serials'}
          </Button>
        </div>
      </form>
      </div>
    </div>,
    document.body,
  );
}
